import {
  Dialog,
  FormStack,
  Stack,
  TextField,
  Typography,
} from '@all-manager/design-system-v2';
import type { ChangeEvent } from 'react';
import ContactNameAutocomplete from '../../../../components/ContactNameAutocomplete/ContactNameAutocomplete';
import ProviderAutocomplete from '../../../../components/ProviderAutocomplete/ProviderAutocomplete';
import SubTopicAutocomplete from '../../../topics/external-components/SubTopicAutocomplete/SubTopicAutocomplete';
import type { InboxEmail } from '../../types/inbox.types';
import { useCreateCommunicationFromInboxModal } from './useCreateCommunicationFromInboxModal';

type Props = {
  open: boolean;
  onClose: () => void;
  email: InboxEmail | null;
  spaceId: string;
  initialSubTopicId?: string | null;
  onSuccess: () => void;
};

export default function CreateCommunicationFromInboxModal(props: Props) {
  const {
    subTopicId,
    setSubTopicId,
    title,
    setTitle,
    content,
    setContent,
    providerName,
    setProviderName,
    contactName,
    setContactName,
    isSubmitting,
    handleConfirm,
  } = useCreateCommunicationFromInboxModal(props);

  if (!props.email) return null;

  function handleTitleChange(e: ChangeEvent<HTMLInputElement>) {
    setTitle(e.target.value);
  }

  function handleContentChange(e: ChangeEvent<HTMLInputElement>) {
    setContent(e.target.value);
  }

  function handleSubTopicChange(newSubTopicId: string | null) {
    setSubTopicId(newSubTopicId || '');
  }

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="md"
      fullWidth
      title="Ajouter comme communication"
      closeLabel="Fermer"
      actions={{
        primaryAction: {
          label: 'Enregistrer la communication',
          onClick: handleConfirm,
          loading: isSubmitting,
          disabled: isSubmitting || !title.trim() || !subTopicId,
        },
        secondaryAction: {
          label: 'Annuler',
          onClick: props.onClose,
          disabled: isSubmitting,
        },
      }}
    >
      <Stack spacing={2}>
        <Typography variant="body2">
          Le contenu de cet email sera enregistré dans l&apos;onglet <strong>Communications</strong> du sujet sélectionné.
        </Typography>

        <FormStack>
          <SubTopicAutocomplete
            label="Sujet associé"
            size="small"
            fullWidth
            required
            value={subTopicId || null}
            onChange={handleSubTopicChange}
          />

          <TextField
            label="Titre de la communication"
            size="small"
            fullWidth
            required
            value={title}
            onChange={handleTitleChange}
          />

          <ContactNameAutocomplete
            label="Expéditeur"
            size="small"
            fullWidth
            value={contactName}
            onChange={setContactName}
          />

          <ProviderAutocomplete
            label="Destinataire"
            size="small"
            fullWidth
            value={providerName}
            onChange={setProviderName}
          />

          <TextField
            label="Contenu du message"
            size="small"
            fullWidth
            multiline
            minRows={6}
            maxRows={12}
            value={content}
            onChange={handleContentChange}
            helperText="Vous pouvez ajuster ou nettoyer le texte de l'email avant de l'enregistrer."
          />
        </FormStack>
      </Stack>
    </Dialog>
  );
}
